/**
 * TimeSlots Container Component
 *
 * Main entry point for the rebuilt timeslot selection UI.
 * Orchestrates data processing, selection state management,
 * sidebar integration, and drag selection functionality.
 *
 * This component:
 * 1. Processes raw data (timeOffs, scheduledAppointments) into TimeSlot[] objects
 * 2. Uses useSelectionHandler for selection state management
 * 3. Uses useSidebarIntegration for sidebar updates
 * 4. Wraps TimeSlotsGrid in DragSelectWrapper for drag selection
 *
 * @requirements All - Core container orchestrating all timeslot functionality
 */

import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react'
import isSameDay from 'date-fns/isSameDay'
import styled from 'styled-components'
import {
  getAvailableTimes,
  getTimeOffsForDay,
  splitAvailableTimes,
} from 'components/common/Month/Tools'
import { TimeSlot } from './types'
import { deriveSlotStatus, RawTimeSlot } from './utils/slotUtils'
import { useSelectionHandler } from './hooks/useSelectionHandler'
import { useSidebarIntegration } from './hooks/useSidebarIntegration'
import DragSelectWrapper from './components/DragSelectWrapper'
import TimeSlotsGrid from './components/TimeSlotsGrid'
import TimeSlotsErrorBoundary from './components/ErrorBoundary'

/**
 * Props for the TimeSlots container component.
 * Matches the existing TimeSlots interface used by SchedulesView.
 */
interface TimeSlotsProps {
  currDate: Date
  timeOffs: any[]
  scheduledAppointments: any[]
}

/**
 * Helper to deduplicate timeslots by ID.
 * Keeps the first occurrence of each unique ID.
 */
const uniqueTimeSlots = (value: any, index: any, self: any) =>
  self.findIndex((v: any) => v.id === value.id) === index

/**
 * Styled container for the TimeSlots component.
 * Matches the existing styling pattern with scroll, padding, and user-select prevention.
 */
const Container = styled.div`
  overflow: auto;
  position: relative;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:focus {
    outline: 2px solid #eb7c0c;
    outline-offset: 2px;
  }

  /* Prevent text selection and dragging during selection */
  * {
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
    -webkit-user-drag: none !important;
    -khtml-user-drag: none !important;
    -moz-user-drag: none !important;
    -o-user-drag: none !important;
  }
`

/**
 * TimeSlots Container Component
 *
 * Processes raw appointment and time-off data into TimeSlot objects,
 * manages selection state, and renders the timeslot grid with drag selection.
 */
const TimeSlots: React.FC<TimeSlotsProps> = ({ currDate, timeOffs, scheduledAppointments }) => {
  // Ref to the scrollable container — passed to DragSelectWrapper for correct box alignment
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Track selected slots for sidebar integration
  const [selectedSlots, setSelectedSlots] = useState<TimeSlot[]>([])

  /**
   * Process raw data into TimeSlot objects with derived status.
   * Uses the same logic as the existing TimeSlots component:
   * 1. Filter appointments for the current day
   * 2. Get time-offs for the day
   * 3. Combine and get available times
   * 4. Split available times into 10-minute intervals
   * 5. Sort by time
   * 6. Deduplicate by time (blocked/booked takes priority over open)
   * 7. Map to TimeSlot objects with derived status
   */
  const timeSlots = useMemo(() => {
    // Filter scheduled appointments for the current day
    const appointmentsForDay = scheduledAppointments?.filter(
      (appointment: any) =>
        appointment.time && isSameDay(new Date(appointment.time), currDate)
    ) || []

    // Get time-offs for the current day, deduplicated
    const timesOffForDay = getTimeOffsForDay(currDate, timeOffs).filter(uniqueTimeSlots)

    // Combine appointments and time-offs
    const eventsForDay = [...appointmentsForDay, ...timesOffForDay]

    // Get available time slots (gaps between events)
    const availableTimes = getAvailableTimes(eventsForDay, currDate)

    // Split available times into smaller intervals
    const extendedAvailableTimes = splitAvailableTimes(availableTimes)

    // Combine all events and available times, sort by time
    const combined = [...extendedAvailableTimes, ...eventsForDay].sort((a: any, b: any) => {
      const aTime = new Date(a.time)
      const bTime = new Date(b.time)
      if (aTime.getHours() < bTime.getHours()) return -1
      if (aTime.getHours() > bTime.getHours()) return 1
      if (aTime.getMinutes() < bTime.getMinutes()) return -1
      return 1
    })

    // Deduplicate by time - keep blocked/booked slots over open slots
    // Use a Map keyed by time string to ensure only one slot per time
    const slotsByTime = new Map<string, any>()
    combined.forEach((item: any) => {
      const timeKey = new Date(item.time).toISOString()
      const existing = slotsByTime.get(timeKey)
      
      if (!existing) {
        // No slot at this time yet, add it
        slotsByTime.set(timeKey, item)
      } else {
        // Slot exists - keep the one that's blocked or booked (has doctorId or patientId)
        const existingIsEvent = existing.doctorId || existing.patientId
        const newIsEvent = item.doctorId || item.patientId
        
        if (newIsEvent && !existingIsEvent) {
          // New item is an event (blocked/booked), replace the open slot
          slotsByTime.set(timeKey, item)
        }
        // Otherwise keep existing (either it's already an event, or both are open)
      }
    })

    // Convert map values back to array and sort
    const deduped = Array.from(slotsByTime.values()).sort((a: any, b: any) => {
      const aTime = new Date(a.time)
      const bTime = new Date(b.time)
      if (aTime.getHours() < bTime.getHours()) return -1
      if (aTime.getHours() > bTime.getHours()) return 1
      if (aTime.getMinutes() < bTime.getMinutes()) return -1
      return 1
    })

    // Default slot duration in milliseconds (10 minutes)
    const DEFAULT_SLOT_DURATION_MS = 10 * 60 * 1000

    // Convert to TimeSlot objects with derived status
    return deduped.map((item: any, idx: number): TimeSlot => {
      const rawSlot: RawTimeSlot = {
        id: item.id || `slot-${idx}-${item.time}`,
        time: new Date(item.time),
        duration: item.duration || DEFAULT_SLOT_DURATION_MS,
        doctorId: item.doctorId,
        patientId: item.patientId,
        appointmentId: item.appointmentId,
        formattedStatus: item.formattedStatus,
      }

      return {
        id: rawSlot.id,
        time: rawSlot.time,
        duration: rawSlot.duration || DEFAULT_SLOT_DURATION_MS,
        status: deriveSlotStatus(rawSlot),
        doctorId: rawSlot.doctorId,
        patientId: rawSlot.patientId,
        appointmentId: rawSlot.appointmentId,
        formattedStatus: rawSlot.formattedStatus,
      }
    })
  }, [currDate, timeOffs, scheduledAppointments])

  /**
   * Callback for when selection changes.
   * Updates the selectedSlots state which is passed to useSidebarIntegration.
   */
  const handleSelectionChange = useCallback((newSelectedSlots: TimeSlot[]) => {
    setSelectedSlots(newSelectedSlots)
  }, [])

  /**
   * Selection state management via useSelectionHandler hook.
   * Provides selected/highlighted slot IDs and all interaction handlers.
   */
  const {
    selectedSlotIds,
    highlightedSlotIds,
    handleSlotClick,
    handleDragSelectionChange,
    handleDragSelectionEnd,
    handleContainerClick,
    clearSelection,
    validateSelection,
  } = useSelectionHandler({
    timeSlots,
    onSelectionChange: handleSelectionChange,
  })

  /**
   * Clear selection when the current date changes.
   * Ensures that navigating to a different date resets any active selection.
   *
   * @requirements 1.7 - Clear all selections when selected date changes
   * @requirements 7.2 - Clear all selections and fetch new timeslot data on date change
   */
  useEffect(() => {
    clearSelection()
  }, [currDate]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Validate selection when timeslots data changes.
   * Removes selected slot IDs that no longer exist in the timeslots array.
   * This handles cases like blocking/unblocking operations that change the timeslot data.
   *
   * @requirement 7.1 - Validate and update selection state on data change
   */
  useEffect(() => {
    validateSelection(timeSlots)
  }, [timeSlots, validateSelection])

  /**
   * Sidebar integration - automatically updates sidebar based on selection state.
   * Shows default sidebar when empty, Event Manager when slots selected,
   * Appointment Details when a booked slot is selected.
   */
  useSidebarIntegration({ selectedSlots })

  /**
   * Handlers for DragSelectWrapper callbacks.
   * The DragSelectWrapper provides (box, elements) but our hooks expect just elements.
   */
  const handleDragStart = useCallback(() => {
    // Optional: could clear previous selection here if needed
  }, [])

  const handleDragChange = useCallback(
    (_box: any, elements: Element[]) => {
      handleDragSelectionChange(elements)
    },
    [handleDragSelectionChange]
  )

  const handleDragEnd = useCallback(
    (_box: any, elements: Element[]) => {
      handleDragSelectionEnd(elements)
    },
    [handleDragSelectionEnd]
  )

  return (
    <TimeSlotsErrorBoundary>
      <Container ref={scrollContainerRef} className="scrollbar-hide" onClick={handleContainerClick}>
        <DragSelectWrapper
          onSelectionStart={handleDragStart}
          onSelectionChange={handleDragChange}
          onSelectionEnd={handleDragEnd}
          enabled
        >
          <TimeSlotsGrid
            timeSlots={timeSlots}
            selectedSlotIds={selectedSlotIds}
            highlightedSlotIds={highlightedSlotIds}
            onSlotClick={handleSlotClick}
          />
        </DragSelectWrapper>
      </Container>
    </TimeSlotsErrorBoundary>
  )
}

export default memo(TimeSlots)
